import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import DynamicSearch from '../../../../search/DynamicSearch';
import LoadingSpinner from '../../../../ui/LoadingSpinner';

import RejectedVacanciesTable from './RejectedVacanciesTable';

import classes from '../../submissionStyles/vacancy.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const RejectedVacanciesTableManagement = ({ isViewOnly = false }) => {
    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rejectedParticipantVacancies, setRejectedParticipantVacancies] = useState([]);
    const [rejectedVacancies, setRejectedVacancies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const vacancyStageDetails = useSelector(
        (state) => state.entities.directusService.vacancyStageDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const businesses = useSelector((state) => state.entities.recruitmentService.businesses);
    const vacanciesForServices = useSelector(
        (state) => state.entities.recruitmentService.vacanciesForServices
    );
    const vacancySubmissions = useSelector(
        (state) => state.entities.recruitmentService.vacancySubmissions
    );
    const usersForVacancies = useSelector((state) => state.entities.userService.usersForVacancies);

    // USE EFFECTS
    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles) || isViewOnly);
    }, [roles]);

    useEffect(() => {
        if (
            vacanciesForServices?.length < 1 ||
            !usersForVacancies ||
            usersForVacancies?.length < 1
        ) {
            setRows([]);
            setRejectedParticipantVacancies([]);
            setRejectedVacancies([]);
            return;
        }

        const rejectedOwnerVacancies = vacanciesForServices.map((el) => {
            const user = usersForVacancies.find((entry) => entry.id === el?.ownerId);
            return {
                ...el,
                businessName:
                    businesses.find((entry) => entry.id === el.businessRecordId)?.name || ' - ',
                adviser: user ? `${user.firstName} ${user.lastName}` : ' - ',
                payRate: `£${el.payRateFrom} - £${el.payRateTo}`,
                postcodeOrRemote: el.remote ? 'Remote' : el.postcode
            };
        });
        setRows(rejectedOwnerVacancies);
        setRejectedParticipantVacancies(rejectedOwnerVacancies);
    }, [vacanciesForServices, usersForVacancies]);

    useEffect(() => {
        if (rejectedParticipantVacancies.length < 1 || vacancySubmissions.length < 1) {
            return;
        }
        const statusId =
            vacancyStageDetails.find((el) => el.name.toLowerCase() === 'rejected')?.id || '';

        const rejectedVacancies = rejectedParticipantVacancies
            .map((el) => {
                const foundVacancySubmission = vacancySubmissions.find(
                    (entry) =>
                        entry.participantId === currentParticipant.id &&
                        entry.vacancyId === el.id &&
                        entry.statusId === statusId
                );
                if (foundVacancySubmission) {
                    return {
                        ...el,
                        feedback: foundVacancySubmission.rejection.feedback,
                        rejectionDate: foundVacancySubmission.rejection.rejectionDate,
                        rejectionReasonIds: foundVacancySubmission.rejection.rejectionReasonIds,
                        jobSectorName:
                            jobSectorDetails.find((entry) => entry.id === el.sectorId)?.name || ''
                    };
                }
            })
            .filter((el) => el);

        if (rejectedVacancies.length > 0) {
            setRows(rejectedVacancies);
            setRejectedVacancies(rejectedVacancies);
        }
    }, [rejectedParticipantVacancies, vacancySubmissions]);

    useEffect(() => {
        const filteredRows = !searchTerm
            ? rows
            : rows.filter((el) => el.title.toLowerCase().includes(searchTerm.toLowerCase()));
        setRejectedVacancies(filteredRows);
    }, [searchTerm]);

    // EVENT HANDLERS
    const onSearch = (searchRes) => setSearchTerm(searchRes);

    // RENDER
    return (
        <>
            <div className={classes.vacanciesToolBar}>
                <DynamicSearch
                    disabled={isDisabled}
                    search={onSearch}
                    searchTerm={searchTerm}
                    placeholder="Enter Search Term"
                />
            </div>

            {rejectedVacancies.length < 1 ? (
                <LoadingSpinner content="No rejected vacancies found" />
            ) : (
                <RejectedVacanciesTable rows={rejectedVacancies} searchTerm={searchTerm} />
            )}
        </>
    );
};

RejectedVacanciesTableManagement.propTypes = {
    isViewOnly: PropTypes.bool
};

export default RejectedVacanciesTableManagement;
